/**
 * The Game — the wristband.
 *
 * One page that holds the whole trained vocabulary, short enough to read in a
 * minute. The words are the owner's, ruled 2026-10-28 (the "chosen words" page
 * of the installed-offense vocabulary, v3). They are quoted, not paraphrased:
 * change a definition here only when the owner rules a different one.
 *
 * Plain page, no data: nothing here is read from or written to the database.
 */

import type { ReactNode } from 'react';
import { BookOpen } from 'lucide-react';
import { Callout, DescriptionList, Panel } from '@/components/ds';
import type { DescriptionItem } from '@/components/ds';
import { PageHeader } from '../../components/prioritization/PageHeader';

/** The date the owner ruled these words. */
const WRISTBAND_RULED_ON = '2026-10-28';

/** A word the page defines, set inside the sentence that defines it. */
function Term({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-[var(--text-pri)]">{children}</strong>;
}

function Prose({ children }: { children: ReactNode }) {
  return <p className="text-sm leading-relaxed text-[var(--text-pri)]">{children}</p>;
}

interface Word {
  word: string;
  /** What the word carries in brackets, e.g. the strengths of "Depends on". */
  note?: string;
}

/**
 * The words of one list, in the order the page gives them. Bare words run on a
 * line, separated by a dot; a word that carries a note takes a line of its own,
 * so a note never breaks the run of words around it.
 */
function WordList({ label, words }: { label: string; words: Word[] }) {
  return (
    <ul role="list" aria-label={label} className="flex flex-wrap items-baseline gap-x-0.5 gap-y-1.5">
      {words.map((w, i) => {
        const next = words[i + 1];
        const dotted = !w.note && next !== undefined && !next.note;
        return (
          <li key={w.word} className={`text-sm text-[var(--text-pri)] ${w.note ? 'basis-full' : ''}`}>
            <span className="font-mono text-[0.8125rem]">{w.word}</span>
            {w.note && <span className="text-[var(--text-sec)]"> ({w.note})</span>}
            {dotted && (
              <span aria-hidden="true" className="px-1.5 text-[var(--text-dim)]">
                ·
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

const plain = (names: string[]): Word[] => names.map((word) => ({ word }));

/* ── The nine slots, each with the one question its reader asks ─────────── */

const NINE_SLOTS: ReadonlyArray<{ slot: string; question: string }> = [
  { slot: 'Subject', question: 'What is this about?' },
  { slot: 'Act', question: 'What was done?' },
  { slot: 'Standing', question: 'How settled is it now?' },
  { slot: 'Authority', question: 'Who did it, and could they?' },
  { slot: 'Frame', question: 'Under what was it judged?' },
  { slot: 'Basis', question: 'Why?' },
  { slot: 'Relations', question: 'What else did it touch?' },
  { slot: 'Lineage', question: 'What did it replace, what came from it?' },
  { slot: 'Time', question: 'When, until when, what lapses it?' },
];

/** The word sits in a fixed-width cell so every question starts at the same place. */
const wordCell = (word: string, width: string): ReactNode => (
  <span className="inline-block" style={{ width }}>
    {word}
  </span>
);

const SLOT_ROWS: DescriptionItem[] = NINE_SLOTS.map(({ slot, question }) => ({
  label: wordCell(slot, '5.5rem'),
  value: question,
}));

/* ── The layers ─────────────────────────────────────────────────────────── */

const LAYER_ROWS: DescriptionItem[] = [
  {
    label: 'The Game',
    value:
      'What every department shares and may not rename: the slots, the words on this page, the ladders, the roles, the relations.',
  },
  {
    label: 'Method',
    value:
      'How a department frames, scores and runs its cadence. The platform ruling already uses that word.',
  },
  { label: 'The wristband', value: 'The one page people are trained on.' },
  {
    label: 'The Steward',
    value: 'The person who corrects a wrong call, in the room and in copy.',
  },
];

/* ── Things (13) ────────────────────────────────────────────────────────── */

const THINGS = [
  'Item',
  'Criterion',
  'Value',
  'Pool',
  'Track',
  'Cycle',
  'Session',
  'Result',
  'Priority List',
  'Plan of Record',
  'Decision',
  'Commitment',
  'Assignment',
];

const THING_ROWS: DescriptionItem[] = [
  {
    label: 'Commitment',
    value: (
      <>
        The undertaking as a whole, a promise to a <Term>Party</Term> (customer, partner,
        regulator, internal), with a due date, lasting across its revisions. The registry row.
      </>
    ),
  },
  {
    label: 'Assignment',
    value:
      'What a named person must now do because a decision was made. It is never made by edit (L3 stands); it is read from a call and bound by acceptance, or by hierarchy where that switch is on. Its content is kept in immutable revisions.',
  },
  {
    label: 'Criterion fields',
    value: (
      <>
        A Criterion names what it measures, its <Term>beneficiary</Term>, its{' '}
        <Term>baseline</Term>, and its <Term>scale</Term>.
      </>
    ),
  },
  {
    label: 'Worth',
    value:
      'How much good an item does under a named criterion, when one must speak generally. Value stays the object.',
  },
  { label: 'Outcome', value: 'What an item points toward.' },
  {
    label: 'Altitude levels',
    value:
      'Numbers, 1 to 5, in the Game; a Method may name its levels, and those names are tags.',
  },
];

/* ── Standings ──────────────────────────────────────────────────────────── */

const LADDER_ROWS: DescriptionItem[] = [
  { label: 'Value', value: 'proposed → official' },
  { label: 'Priority List', value: 'proposed → ratified → published → superseded' },
  { label: 'Plan', value: 'scenario → plan of record → replaced' },
  {
    label: 'Decision',
    value: 'active → superseded or reversed, lapsed when its review date passes',
  },
  {
    label: 'Assignment',
    value:
      'binding (on one of three bases: accepted, by hierarchy, external evidence) → fulfilled, released or invalidated',
  },
];

const READOUT_ROWS: DescriptionItem[] = [
  { label: wordCell('Band', '4.5rem'), value: 'in firm / in at risk / out' },
  { label: wordCell('Backing', '4.5rem'), value: 'announced / backed / no basis' },
  { label: wordCell('Term', '4.5rem'), value: 'armed / tripped / resolved' },
];

/* ── Acts ───────────────────────────────────────────────────────────────── */

const ACTS: Word[] = [
  { word: 'Triage' },
  { word: 'Propose' },
  { word: 'Accept' },
  { word: 'Lock' },
  { word: 'Rank' },
  { word: 'Publish' },
  { word: 'Ratify' },
  { word: 'Acknowledge' },
  { word: 'Authorize' },
  { word: 'Claim', note: 'resources; the item becomes backed' },
  { word: 'Arm' },
  { word: 'Supersede' },
  { word: 'Reopen' },
  { word: 'Override' },
  { word: 'Contest', note: 'record that you did not agree, with a reason' },
  { word: 'Readback', note: 'a bound party states what they will do differently' },
];

/** Words the record never uses as acts. They appear on this page only to be refused. */
const BANNED_ACTS = 'approve, commit, align, finalize, decide';

/* ── Roles ──────────────────────────────────────────────────────────────── */

const ROLES = [
  'Item Manager',
  'Owner',
  'Participant',
  'Facilitator',
  'Ball Holder',
  'Evaluator',
  'Decision Authority',
  'Steward',
];

/* ── Relations ──────────────────────────────────────────────────────────── */

const RELATIONS: Word[] = [
  { word: 'Same as' },
  { word: 'Part of' },
  { word: 'Package' },
  { word: 'Competing' },
  { word: 'Depends on', note: 'informational, soft, hard, gate' },
  { word: 'Related' },
  { word: 'Derived from' },
  {
    word: 'Carries',
    note: 'an item carries part of the worth of what it enables, with magnitude',
  },
];

export default function WristbandPage() {
  return (
    <div className="mx-auto w-full max-w-3xl space-y-4">
      <PageHeader
        icon={<BookOpen className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="The Game"
        subtitle={`The wristband, ruled ${WRISTBAND_RULED_ON}`}
        mb="mb-2"
      />

      <p className="font-serif text-lg leading-snug text-[var(--text-pri)]">
        The words the record uses. Everything else is a tag.
      </p>

      <Panel title="The standard" bodyClassName="space-y-3">
        <Prose>
          A record <Term>holds</Term> when anyone, at any level, any later time, in any system,
          reads the same consequential meaning from it without the people who made it.
        </Prose>
        <Prose>
          The test is the <Term>stranger test</Term>: one sentence, handed to someone who was not
          there. When two readers would act differently, the record <Term>broke</Term>. (Same
          family as the Signal Verdict&apos;s held and broke: what was written stayed true, or did
          not.)
        </Prose>
      </Panel>

      <Panel title="The unit">
        <Prose>
          A <Term>call</Term>: one consequential thing said, with nine <Term>slots</Term>. A call
          with every slot filled is <Term>whole</Term>. A whole call at the top of its ladder,
          reached by ceremony, is an <Term>Agreement</Term> (02B&apos;s word; not a new object, a
          name for that state).
        </Prose>
      </Panel>

      <Panel title="The nine slots">
        <DescriptionList items={SLOT_ROWS} columns={1} layout="inline" divided />
      </Panel>

      <Panel title="The layers">
        <DescriptionList items={LAYER_ROWS} columns={1} divided />
      </Panel>

      <Panel title="Things" count={THINGS.length} bodyClassName="space-y-4">
        <WordList label="Things" words={plain(THINGS)} />
        <DescriptionList items={THING_ROWS} columns={1} divided />
      </Panel>

      <Panel title="Standings" bodyClassName="space-y-4">
        <DescriptionList items={LADDER_ROWS} columns={1} divided />
        <Panel inset label="Readouts on a Plan of Record">
          <DescriptionList items={READOUT_ROWS} columns={1} layout="inline" divided />
        </Panel>
        <Prose>
          Planning&apos;s capacity condition is <Term>Out - capacity</Term>; <Term>Deferred</Term>{' '}
          belongs to a person&apos;s triage choice only.
        </Prose>
      </Panel>

      <Panel title="Acts" bodyClassName="space-y-4">
        <WordList label="Acts" words={ACTS} />
        <Prose>
          A change to an Assignment is a <Term>Disposition</Term>: amend, suspend, resume,
          transfer, release, fulfill, invalidate.
        </Prose>
        <Callout tone="mustard" label="Banned as acts">
          {BANNED_ACTS}
        </Callout>
      </Panel>

      <Panel title="Roles" bodyClassName="space-y-3">
        <WordList label="Roles" words={plain(ROLES)} />
        <Prose>
          A field&apos;s system of record is its <Term>Source</Term>, never its owner.
        </Prose>
      </Panel>

      <Panel title="Relations" bodyClassName="space-y-3">
        <WordList label="Relations" words={RELATIONS} />
        <Prose>
          Planning&apos;s timing relation is a <Term>Sequence link</Term>, not a dependency. A
          Decision <Term>rests on</Term> its elements, never &quot;depends on&quot; them.
        </Prose>
      </Panel>

      <p className="pb-2 font-serif text-base text-[var(--text-sec)]">That is the whole Game.</p>
    </div>
  );
}
